import type { FC, ReactElement } from 'react';

import { useTranslation } from '../../api/useTranslation';
import { StringFormatter } from '../../formatters/StringFormatter';

declare global {
  interface Window {
    unknownKeys?: Record<string, number>;
  }
}

export interface L12nProps {
  /**
   * The translation key string used to fetch the localized text.
   */
  tk: string;
  /**
   * An array of strings used for dynamic values substitution within the text.
   */
  dynamicValues?: string[] | Record<string, string>;
  /**
   * An boolean flag to enable markdown parsing on the localized text.
   */
  markdown?: boolean;
}

export const L12nText: FC<L12nProps> = ({ tk, dynamicValues, markdown }): ReactElement | string => {
  const { translations, isFetching } = useTranslation();

  if (isFetching) {
    return '\u00A0';
  }

  if (translations[tk] === undefined) {
    console.warn(`[L12nText] "${tk}" is not found`);
    const unknownKeys = window.unknownKeys || {};
    unknownKeys[tk] = 1;
    window.unknownKeys = unknownKeys;
    return tk;
  }

  const text: string = dynamicValues ? StringFormatter(translations[tk], dynamicValues) : translations[tk];

  // Very Slow - Use with caution
  if (markdown) {
    const htmlText = parseMarkdown(text);
    return <span dangerouslySetInnerHTML={{ __html: htmlText }} />;
  }

  return text;
};

const markdownBoldRegex = /\*\*(.*?)\*\*/g;
const markdownUnderlinedReg = /__(.*?)__/g;
// Utils
function parseMarkdown(text: string): string {
  return (
    text
      // Replace ** with <b></b> for bold text
      .replace(markdownBoldRegex, '<b>$1</b>')
      // Replace __ with <u></u> for underlined text
      .replace(markdownUnderlinedReg, '<u>$1</u>')
  );
}
